import { Edit2, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useAddPurchaseEntry,
  useUpdatePurchaseEntry,
} from "../service/purchaseEntryService";
import { useGetAllProductData } from "../service/productService";
import { ReusableCombobox } from "../utils/components/ui/combobox";
import {
  PurchaseSchema,
  PurchaseSchemaT,
} from "../types/schema/purchaseEntery";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { cn } from "../utils/cn";
import { useGetPurchaseReturnEntry } from "../service/purchaseEntryReturnService";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAllVendors } from "../service/vendorService";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";
import Modal from "../components/common/Modal";
import {
  calculateBaseQuantity,
  calculateUnitPrice,
  getDefaultUnitOption,
  getUnitOption,
  normalizeUnitOptions,
} from "../utils/unitConversion";
import { findProductById } from "../utils/productDropdown";

type Product = {
  altId: number;
  id: number;
  name: string;
  quantity: number;
  unitPrice: number;
  tax: number;
  discount: number;
  total: number;
  productId?: number;
  type?: string;
  unit: string;
  conversionToBaseUnit: number;
  baseQuantity: number;
};

const paymentModeOptions = [
  { id: "CARD", name: "CARD" },
  { id: "CHEQUE", name: "CHEQUE" },
  { id: "CASH", name: "CASH" },
  { id: "CREDIT", name: "CREDIT" },
  { id: "ONLINE_PAYMENT", name: "ONLINE PAYMENT" },
  { id: "MIXED", name: "MIXED" },
];

function NewPurchaseForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { id } = useParams();
  console.log(id, "id from params");
  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const { data: purchaseReturnData } = useGetPurchaseReturnEntry({
    purchaseNumber: id,
  });

  useEffect(() => {
    if (
      purchaseReturnData?.purchaseEntryProductResponse &&
      products.length === 0
    ) {
      const existingProducts =
        purchaseReturnData.purchaseEntryProductResponse.map(
          (product, index) => ({
            altId: product.id,
            productId: product.productId?.toString() || "",
            name: product.productName || "",
            quantity: product.quantity?.toString() || "0",
            unitPrice: product.unitPrice?.toString() || "0",
            tax: product.tax?.toString() || "0",
            discount: product.discount?.toString() || "0",
            total: product.totalAmount || 0,
            unit: product.unit || "",
            conversionToBaseUnit: product.conversionToBaseUnit || 1,
            baseQuantity: product.baseQuantity || product.quantity || 0,
          }),
        );
      setProducts(existingProducts);
    }
  }, [purchaseReturnData]);

  // Search state
  const [productSearch, setProductSearch] = useState(" ");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [vendorSearch, setVendorSearch] = useState("");

  const { data: allProductsData } = useGetAllProductData({
    pageNo: 0,
    pageSize: 100,
    keyword: productSearch.trim() || undefined,
  });
  const allProducts = allProductsData?.productResponseList || [];
  const { data: vendorsResponse } = useGetAllVendors({
    search: vendorSearch,
    status: "ACTIVE",
    pageNo: 0,
    pageSize: 100,
  });

  // Current product input
  const [currentProduct, setCurrentProduct] = useState({
    id: 0,
    productId: "",
    name: "",
    quantity: 0,
    unitPrice: 0,
    tax: 5,
    discount: 0,
    type: "%",
    unit: "",
    conversionToBaseUnit: 1,
    baseQuantity: 0,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,

    formState: { errors },
  } = useForm<PurchaseSchemaT>({
    resolver: zodResolver(PurchaseSchema),
    defaultValues: {
      vendorId: 0,
      vendorName: "",
      purchaseEntryProductRequests: [
        {
          productId: 0,
          quantity: 0,
          unitPrice: 0,
          tax: 0,
          discount: 0,
          expiryDate: "",
          isDiscountPercentage: currentProduct.type === "%",
          unit: "",
          conversionToBaseUnit: 1,
          baseQuantity: 0,
        },
      ],
      amountPaid: purchaseReturnData?.amountPaid ?? 0,
      paymentMode: "CASH",
      chqNumber: "",
      cardNumber: "",
      onlinePaymentRefNo: "",
    },
  });

  useEffect(() => {
    if (purchaseReturnData) {
      setValue("vendorId", Number(purchaseReturnData.vendorId) || 0);
      setValue("vendorName", purchaseReturnData.vendorName || "");
      setValue("purchaseNumber", purchaseReturnData.purchaseNumber || "");
      setValue("amountPaid", purchaseReturnData.amountPaid || 0);
      setValue("paymentMode", purchaseReturnData.paymentMode || "CASH");
      // Convert dd-MM-yyyy to yyyy-MM-dd for the date input
      if (purchaseReturnData.purchaseDate) {
        const dateStr = purchaseReturnData.purchaseDate;
        const parts = dateStr.split("-");
        if (parts.length === 3 && parts[0].length <= 2) {
          setValue("purchaseDate", `${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
          setValue("purchaseDate", dateStr);
        }
      }
    }
  }, [purchaseReturnData, setValue]);

  // Handle adding a new productF
  const addOrUpdateProduct = () => {
    // console.log(products[editingIndex].id);
    if (!currentProduct.productId || !currentProduct.quantity) {
      toast.error("Please select a product and enter quantity");
      return;
    }

    const total = calculateTotal(currentProduct);
    const productData = {
      altId:
        editingIndex !== null
          ? products[editingIndex].altId
          : currentProduct.id, // Use currentProduct.id for new products

      id:
        editingIndex !== null
          ? products[editingIndex].id
          : currentProduct.id || 0,
      name: currentProduct.name,
      productName: currentProduct.name,
      quantity: currentProduct.quantity,
      unitPrice: currentProduct.unitPrice,
      tax: currentProduct.tax,
      discount: currentProduct.discount,
      total,
      totalAmount: total,
      productId: currentProduct.productId,
      type: currentProduct.type,
      unit: currentProduct.unit,
      conversionToBaseUnit: currentProduct.conversionToBaseUnit,
      baseQuantity: calculateBaseQuantity(
        currentProduct.quantity,
        currentProduct.conversionToBaseUnit,
      ),
    };

    console.log(productData, "productData to add or update");

    if (editingIndex !== null) {
      // Update existing product
      const updatedProducts = [...products];
      updatedProducts[editingIndex] = productData;
      setProducts(updatedProducts);
      setEditingIndex(null);
      toast.success("Product updated successfully");
    } else {
      // Add new product
      setProducts([...products, productData]);
      toast.success("Product added successfully");
    }

    resetCurrentProduct();
  };

  const editProduct = (index: number) => {
    const product = products[index];
    setCurrentProduct({
      id: product.altId,
      productId: product.productId?.toString() || "",
      name: product.name || "",
      quantity: product.quantity || 0,
      unitPrice: product.unitPrice || 0,
      tax: product.tax || 0,
      discount: product.discount || 0,
      type: product.type || "%",
      unit: product.unit,
      conversionToBaseUnit: product.conversionToBaseUnit,
      baseQuantity: product.baseQuantity,
    });
    setEditingIndex(index);
    toast.info("Product loaded for editing");
  };

  // Calculate total based on inputs
  const calculateTotal = (product: typeof currentProduct) => {
    const quantity = parseFloat(product.quantity) || 0;
    const unitPrice = parseFloat(product.unitPrice) || 0;
    const tax = parseFloat(product.tax) || 0;
    const discount = parseFloat(product.discount) || 0;

    const subtotal = quantity * unitPrice;
    // Calculate discount first
    const discountAmount =
      product.type === "%" ? subtotal * (discount / 100) : discount;
    const discountedSubtotal = subtotal - discountAmount;
    // Then calculate tax on discounted amount
    const taxAmount = discountedSubtotal * (tax / 100);

    return discountedSubtotal + taxAmount;
  };

  const resetCurrentProduct = () => {
    setCurrentProduct({
      id: 0,
      productId: "",
      name: "",
      quantity: 0,
      unitPrice: 0,
      tax: 5,
      discount: 0,
      type: "%",
      unit: "",
      conversionToBaseUnit: 1,
      baseQuantity: 0,
    });
    setEditingIndex(null);
  };

  // Remove a product
  const removeProduct = (index: number) => {
    console.log("Removing product at index:", index);
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);

    // If we were editing this product, reset the form
    if (editingIndex === index) {
      resetCurrentProduct();
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index if needed
      setEditingIndex(editingIndex - 1);
    }

    toast.success("Product removed successfully");
  };

  const { mutate: addPurchaseEntry, isPending: isaddPurchaseEntryPending } =
    useAddPurchaseEntry();
  const {
    mutate: updatePurchaseEntry,
    isPending: isUpdatePurchaseEntryPending,
  } = useUpdatePurchaseEntry();

  const isPending = isaddPurchaseEntryPending || isUpdatePurchaseEntryPending;

  const onSubmit = (data: PurchaseSchemaT) => {
    // Map your products state to the required structure
    delete data?.id;
    const purchaseEntryProductRequests = products.map((p) => ({
      id: Number(p?.altId),
      productId: Number(p.productId),
      quantity: Number(p.quantity),
      unitPrice: Number(p.unitPrice),
      tax: Number(p.tax),
      discount: Number(p.discount),
      expiryDate: "", // Add expiryDate if you have it in your product state
      isDiscountPercentage: p.type === "%",
      unit: p.unit,
      conversionToBaseUnit: Number(p.conversionToBaseUnit),
      baseQuantity: calculateBaseQuantity(p.quantity, p.conversionToBaseUnit),
    }));

    const postData = {
      ...data,
      vendorId: Number(data.vendorId),
      purchaseEntryProductRequests,
      attachment: data.attachment || "",
      paymentMode: data.paymentMode || "CASH",
      chqNumber: data.chqNumber || "",
      cardNumber: data.cardNumber || "",
      onlinePaymentRefNo: data.onlinePaymentRefNo || "",
    };
    if (purchaseReturnData?.id) {
      postData.id = Number(purchaseReturnData?.id);
      updatePurchaseEntry(postData, {
        onSuccess: () => {
          toast.success("Purchase entry updated successfully!");
          navigate("/purchase");
          queryClient.invalidateQueries({
            queryKey: ["useGetPurchaseReturnEntry"],
          });
          setProducts([]);
          resetCurrentProduct();
        },
        onError: (error: Error) => {
          console.error("Error updating purchase entry", error);
          toast.error(error.message);
        },
      });
    } else {
      postData.id = 0; // Set id to 0 for new entries
      addPurchaseEntry(postData, {
        onSuccess: () => {
          toast.success("Purchase entry added successfully!");
          navigate("/purchase");
          queryClient.invalidateQueries({
            queryKey: ["useGetPurchaseReturnEntry"],
          });
          setProducts([]);
          resetCurrentProduct();
        },
        onError: (error: Error) => {
          console.error("Error adding purchase entry", error);
          toast.error(error.message);
        },
      });
    }
  };

  // Calculate totals for all products
  const calculateTotals = () => {
    const totals = products.reduce(
      (acc, product) => {
        const quantity = parseFloat(product.quantity) || 0;
        const unitPrice = parseFloat(product.unitPrice) || 0;
        const tax = parseFloat(product.tax) || 0;
        const discount = parseFloat(product.discount) || 0;

        const subtotal = quantity * unitPrice;
        const discountAmount =
          product.type === "%" ? subtotal * (discount / 100) : discount;
        const discountedSubtotal = subtotal - discountAmount;
        const taxAmount = discountedSubtotal * (tax / 100);

        acc.netTotal += subtotal;
        acc.totalTax += taxAmount;
        acc.totalDiscount += discountAmount;
        acc.grandTotal += discountedSubtotal + taxAmount;

        return acc;
      },
      { netTotal: 0, totalTax: 0, totalDiscount: 0, grandTotal: 0 },
    );

    return totals;
  };

  const totals = calculateTotals();
  const vendorOptions =
    vendorsResponse?.content?.map((vendor) => ({
      value: String(vendor.id),
      label: vendor.vendorName,
    })) || [];

  const [vendorModal, setVendorModal] = useState(false);
  useEffect(() => {
    if (vendorOptions.length === 0) {
      setVendorModal(true);
    } else {
      setVendorModal(false);
    }
  }, [vendorOptions.length]);

  const paymentModePills = [
    { id: "CARD", label: "CARD" },
    { id: "CHEQUE", label: "CHEQUE" },
    { id: "CASH", label: "CASH" },
    { id: "CREDIT", label: "CREDIT" },
    { id: "ONLINE_PAYMENT", label: "ONLINE" },
  ] as const;

  const inputCls =
    "h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
  const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/purchase">Purchase</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Create Purchase</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page title */}
        <h1 className="text-2xl font-bold text-[#1A5EF9]">Create Purchase</h1>

        {/* Purchase Details card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Purchase Details
          </h2>

          {/* 3-column grid: Vendor Name, Purchase Date, Purchase Number */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {/* Vendor Name */}
            <div>
              <label className={labelCls}>Vendor Name</label>
              <ReusableCombobox
                options={vendorOptions}
                value={String(watch("vendorId") || "")}
                onValueChange={(value) => {
                  const selectedVendor = vendorsResponse?.content?.find(
                    (vendor) => String(vendor.id) === String(value),
                  );
                  setValue("vendorId", Number(value), { shouldValidate: true });
                  setValue("vendorName", selectedVendor?.vendorName || "", {
                    shouldValidate: true,
                  });
                }}
                placeholder="Select vendor"
                searchPlaceholder="Search vendor..."
                emptyText="No vendor found."
                className="w-full justify-between bg-[#F5F8FD] text-[#23324D]"
                onInputChange={setVendorSearch}
              />
              <input
                type="hidden"
                {...register("vendorId", { valueAsNumber: true })}
              />
              <input type="hidden" {...register("vendorName")} />
              {errors.vendorId && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.vendorId.message}
                </span>
              )}
              {errors.vendorName && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.vendorName.message}
                </span>
              )}
            </div>

            {/* Purchase Date */}
            <div>
              <label className={labelCls}>Purchase Date</label>
              <input
                type="date"
                {...register("purchaseDate")}
                className={inputCls}
                defaultValue={purchaseReturnData?.purchaseDate || ""}
                required
              />
              {errors.purchaseDate && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.purchaseDate.message}
                </span>
              )}
            </div>

            {/* Purchase Number */}
            <div>
              <label className={labelCls}>Purchase Number</label>
              <input
                type="text"
                {...register("purchaseNumber")}
                className={inputCls}
                placeholder="Enter Purchase Number"
                defaultValue={purchaseReturnData?.purchaseNumber || ""}
              />
              {errors.purchaseNumber && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.purchaseNumber.message}
                </span>
              )}
            </div>
          </div>

          {/* Payment Mode — full width row with pill buttons */}
          <div>
            <label className={labelCls}>Payment Mode</label>
            <div className="flex flex-wrap gap-2">
              {paymentModePills.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() =>
                    setValue(
                      "paymentMode",
                      mode.id as PurchaseSchemaT["paymentMode"],
                      {
                        shouldValidate: true,
                      },
                    )
                  }
                  className={cn(
                    "rounded-[10px] px-4 py-2 text-sm font-semibold transition-colors",
                    watch("paymentMode") === mode.id
                      ? "bg-[#1A5EF9] text-white"
                      : "bg-[#F4F7FC] text-[#415043] hover:bg-[#E8EEF9]",
                  )}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            {errors.paymentMode && (
              <span className="text-red-500 text-xs mt-1">
                {errors.paymentMode.message}
              </span>
            )}
          </div>

          {/* Payment reference fields — shown based on selected payment mode */}
          {watch("paymentMode") === "CHEQUE" && (
            <div className="mt-4">
              <label className={labelCls}>Cheque Number</label>
              <input
                type="text"
                {...register("chqNumber")}
                className={inputCls}
                placeholder="Enter cheque number"
              />
              {errors.chqNumber && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.chqNumber.message}
                </span>
              )}
            </div>
          )}

          {watch("paymentMode") === "CARD" && (
            <div className="mt-4">
              <label className={labelCls}>Card Number</label>
              <input
                type="text"
                {...register("cardNumber")}
                className={inputCls}
                placeholder="Enter card number (last 4 digits)"
              />
              {errors.cardNumber && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.cardNumber.message}
                </span>
              )}
            </div>
          )}

          {watch("paymentMode") === "ONLINE_PAYMENT" && (
            <div className="mt-4">
              <label className={labelCls}>Online Payment Reference No</label>
              <input
                type="text"
                {...register("onlinePaymentRefNo")}
                className={inputCls}
                placeholder="Enter payment reference number"
              />
              {errors.onlinePaymentRefNo && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.onlinePaymentRefNo.message}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Add Product card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Add Product
          </h2>

          {/* Product combobox — full width */}
          <div className="mb-5">
            <label className={labelCls}>Product Name</label>
            <ReusableCombobox
              options={
                allProducts
                  ? allProducts.map((p) => ({
                      value: String(p.id),
                      label: p.productName,
                    }))
                  : []
              }
              value={String(currentProduct.productId)}
              onValueChange={(val) => {
                const selected = allProducts?.find((p) => String(p.id) === String(val));
                const defaultUnit = getDefaultUnitOption(
                  selected?.unitOptions,
                  selected?.unit,
                );
                setCurrentProduct({
                  ...currentProduct,
                  productId: val,
                  name: selected?.productName || "",
                  unitPrice: calculateUnitPrice(
                    selected?.buyingPrice || selected?.price || 0,
                    defaultUnit?.conversionToBaseUnit || 1,
                  ),
                  quantity: 0,
                  tax: selected?.tax || 5,
                  unit: defaultUnit?.unit || selected?.unit || "",
                  conversionToBaseUnit: defaultUnit?.conversionToBaseUnit || 1,
                  baseQuantity: 0,
                });
              }}
              placeholder="Search product"
              className="w-full font-normal bg-[#F5F8FD] text-[#23324D]"
              onInputChange={setProductSearch}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-8 gap-4 mb-5">
            <div>
              <label className={labelCls}>Qty</label>
              <input
                type="text"
                value={currentProduct.quantity}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    quantity: e.target.value ? parseFloat(e.target.value) : 0,
                  })
                }
                className={inputCls}
                placeholder="0"
              />
            </div>

            <div>
              <label className={labelCls}>Unit</label>
              <select
                value={currentProduct.unit}
                onChange={(event) => {
                  const selected = findProductById(
                    allProducts,
                    currentProduct.productId,
                  );
                  const option = getUnitOption(
                    selected?.unitOptions,
                    event.target.value,
                    selected?.unit,
                  );
                  setCurrentProduct({
                    ...currentProduct,
                    unit: event.target.value,
                    conversionToBaseUnit: option?.conversionToBaseUnit || 1,
                    unitPrice: calculateUnitPrice(
                      selected?.buyingPrice || 0,
                      option?.conversionToBaseUnit || 1,
                    ),
                  });
                }}
                className={inputCls}
              >
                {normalizeUnitOptions(
                  findProductById(allProducts, currentProduct.productId)
                    ?.unitOptions,
                  findProductById(allProducts, currentProduct.productId)?.unit,
                ).map((option) => (
                  <option key={option.unit} value={option.unit}>
                    {option.unit}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Base Qty</label>
              <input
                type="text"
                readOnly
                value={calculateBaseQuantity(
                  currentProduct.quantity,
                  currentProduct.conversionToBaseUnit,
                )}
                className={cn(inputCls, "cursor-not-allowed opacity-60")}
              />
            </div>

            <div>
              <label className={labelCls}>Unit Price</label>
              <input
                type="number"
                value={currentProduct.unitPrice}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    unitPrice: e.target.value ? parseFloat(e.target.value) : 0,
                  })
                }
                className={inputCls}
                placeholder="0.000"
              />
            </div>

            <div>
              <label className={labelCls}>Tax %</label>
              <input
                type="text"
                value={currentProduct.tax}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    tax: e.target.value ? parseFloat(e.target.value) : 0,
                  })
                }
                className={inputCls}
                placeholder="0"
              />
            </div>

            <div>
              <label className={labelCls}>Discount</label>
              <input
                type="text"
                value={currentProduct.discount}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    discount: e.target.value ? parseFloat(e.target.value) : 0,
                  })
                }
                className={inputCls}
                placeholder="0"
              />
            </div>

            <div>
              <label className={labelCls}>Discount Type</label>
              <select
                value={currentProduct.type}
                onChange={(e) =>
                  setCurrentProduct({ ...currentProduct, type: e.target.value })
                }
                className={inputCls}
              >
                <option value="%">%</option>
                <option value="FLAT">FLAT</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>Total</label>
              <input
                type="text"
                value={calculateTotal(currentProduct).toFixed(3)}
                readOnly
                className={cn(inputCls, "cursor-not-allowed opacity-60")}
                placeholder="Auto"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-2">
            {editingIndex !== null && (
              <button
                onClick={resetCurrentProduct}
                type="button"
                className="h-11 rounded-[12px] border border-[#E5ECF8] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
              >
                Cancel Edit
              </button>
            )}
            <button
              onClick={addOrUpdateProduct}
              type="button"
              disabled={!currentProduct.productId || !currentProduct.quantity}
              className={cn(
                "h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors",
                (!currentProduct.productId || !currentProduct.quantity) &&
                  "opacity-50 cursor-not-allowed",
              )}
            >
              <Plus size={16} />
              {editingIndex !== null ? "Update Product" : "Add Product"}
            </button>
          </div>
        </div>

        {/* Products table card */}
        <PurchaseProductsTable
          products={products}
          removeProduct={removeProduct}
          editProduct={editProduct}
          editingIndex={editingIndex}
        />

        {/* Summary card */}
        <div className="flex justify-end">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-[#21314A] mb-5">
              Summary
            </h2>

            <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
              <span className="text-sm text-[#52627C]">Net Total</span>
              <span className="text-sm font-medium text-[#23324D]">
                {totals.netTotal.toFixed(3)}
              </span>
            </div>

            <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
              <span className="text-sm text-[#52627C]">Discount</span>
              <span className="text-sm font-medium text-[#23324D]">
                {totals.totalDiscount.toFixed(3)}
              </span>
            </div>

            <div className="flex justify-between items-center pt-3 mb-5">
              <span className="text-base font-bold text-[#1A5EF9]">
                Grand Total
              </span>
              <span className="text-base font-bold text-[#1A5EF9]">
                {totals.grandTotal.toFixed(3)}
              </span>
            </div>

            <div className="mb-5">
              <label className={labelCls}>Amount Paid</label>
              <input
                type="number"
                {...register("amountPaid", {
                  valueAsNumber: true,
                })}
                className={inputCls}
                placeholder="Enter amount"
                step="any"
              />
              {errors.amountPaid && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.amountPaid.message}
                </span>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className={cn(
                  "h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors",
                  isPending && "opacity-70 cursor-not-allowed",
                )}
              >
                {isPending && <Loader2 className="animate-spin w-4 h-4" />}
                Save Purchase
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* No Vendors Modal */}
      <Modal open={vendorModal}>
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-sm">
          <h2 className="text-lg font-semibold text-[#21314A] mb-2">
            No Vendors Found
          </h2>
          <p className="text-sm text-[#52627C] mb-5">
            There are no vendors in the system yet. Add a vendor to get started
            with purchases.
          </p>
          <button
            type="button"
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center gap-2 transition-colors"
            onClick={() => {
              navigate("/vendor-management");
            }}
          >
            Add Vendor
          </button>
        </div>
      </Modal>
    </>
  );
}

export default NewPurchaseForm;

interface PurchaseProductsTableProps {
  products: Product[];
  removeProduct: (index: number) => void;
  editProduct: (index: number) => void;
  editingIndex: number | null;
}

const PurchaseProductsTable = ({
  editProduct,
  products,
  removeProduct,
  editingIndex,
}: PurchaseProductsTableProps) => (
  <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
    <h2 className="text-lg font-semibold text-[#21314A] mb-5">Products</h2>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
            <th className="px-4 py-3 rounded-l-[10px]">Sl.</th>
            <th className="px-4 py-3">Product</th>
            <th className="px-4 py-3">Quantity</th>
            <th className="px-4 py-3">Unit Price</th>
            <th className="px-4 py-3">Tax %</th>
            <th className="px-4 py-3">Discount</th>
            <th className="px-4 py-3">Total</th>
            <th className="px-4 py-3 rounded-r-[10px]">Action</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => (
            <tr
              key={product.id}
              className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
            >
              <td className="px-4 py-3">{index + 1}</td>
              <td className="px-4 py-3 font-medium text-[#23324D]">
                {product.name}
              </td>
              <td className="px-4 py-3">
                {product.quantity || "0"} {product.unit}
              </td>
              <td className="px-4 py-3">{product.unitPrice || "0.000"}</td>
              <td className="px-4 py-3">{product.tax || "0"}</td>
              <td className="px-4 py-3">{product.discount || "0"}</td>
              <td className="px-4 py-3 font-medium text-[#23324D]">
                OMR {product.total?.toFixed(3)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => editProduct(index)}
                    type="button"
                    className="p-1.5 rounded-[8px] text-[#1A5EF9] hover:bg-[#EEF3FF] transition-colors"
                    title="Edit product"
                    aria-label="Edit product"
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    onClick={() => removeProduct(index)}
                    type="button"
                    disabled={editingIndex !== null}
                    className="p-1.5 rounded-[8px] text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Remove product"
                    aria-label="Remove product"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {products.length === 0 && (
        <div className="text-center py-10 text-sm text-[#52627C]">
          No products added yet.
        </div>
      )}
    </div>
  </div>
);
